import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { api, type Me } from '../../lib/api'
import { partageEntrant } from '../../lib/partage'
import { layout } from '../../design/tokens'
import { AuthScreen } from '../auth/AuthScreen'
import { ChangementImpose } from '../auth/ChangementImpose'
import { CategoryMark } from '../spaces/CategoryMark'
import { AddSheet, type DepartDAjout } from './AddSheet'

/**
 * Ce qui se passe quand on partage vers l'application depuis le téléphone.
 *
 * Jusqu'ici, ajouter une recette vue sur Instagram demandait de copier l'adresse, de quitter
 * l'application, d'ouvrir celle-ci, d'appuyer sur « + », sur « Lien », puis de coller. Six
 * gestes pour un geste. Android sait faire mieux depuis que le site a son certificat : la
 * cible de partage déclarée au manifeste (L130) nous fait apparaître dans le menu « Partager ».
 *
 * L'écran s'ouvre directement sur le formulaire rempli : il ne reste qu'à enregistrer.
 */
export function PartageRoute() {
  const [parametres] = useSearchParams()
  const navigate = useNavigate()

  const { data: me, isPending } = useQuery<Me>({ queryKey: ['me'], queryFn: api.me })

  const [choisi, setChoisi] = useState<string | null>(null)
  const [forcerLeChoix, setForcerLeChoix] = useState(false)

  const partage = partageEntrant(parametres)

  if (isPending) {
    return (
      <main
        className="mx-auto min-h-dvh bg-surface"
        style={{ maxWidth: layout.column, paddingInline: layout.gutter }}
      >
        <div className="mt-16 rounded-field bg-sunken" style={{ height: 20, width: '60%' }} />
      </main>
    )
  }

  // Non connecté : l'écran de connexion habituel. Le partage est perdu, et c'est assumé — le
  // retenir en attendant une connexion supposerait de le garder quelque part, pour un cas qui
  // ne se présente qu'une fois.
  if (!me) {
    return <AuthScreen />
  }

  if (me.mustChangePassword) {
    return <ChangementImpose me={me} />
  }

  const espace = choisi ?? me.activeSpaceId

  if (espace === null || forcerLeChoix || me.spaces.length === 0) {
    return (
      <ChoixDEspace
        me={me}
        partage={partage.lien ?? partage.titre}
        onChoix={(identifiant) => {
          setChoisi(identifiant)
          setForcerLeChoix(false)
        }}
      />
    )
  }

  // Le formulaire d'un lien n'a pas de champ de texte — la fiche tient sa description de
  // l'aperçu de la page —, donc la légende qui accompagnait le partage n'y a pas de place et
  // n'est pas transmise. Pour une note, en revanche, elle fait tout le contenu.
  const depart: DepartDAjout = partage.lien
    ? { tuile: 'lien', valeur: partage.lien, note: '' }
    : { tuile: 'note', valeur: partage.titre ?? '', note: partage.texte ?? '' }

  const nom = me.spaces.find((candidat) => candidat.id === espace)?.name

  // Où cela va atterrir, dit avant d'enregistrer : un partage traverse l'application sans
  // qu'on ait choisi d'espace, et découvrir après coup que la recette est allée dans
  // « Famille » obligerait à la refaire.
  const entete =
    me.spaces.length > 1 && nom ? (
      <p className="mb-3 flex items-center justify-between text-meta">
        <span className="text-ink-muted">
          Dans <span className="font-semibold text-ink">{nom}</span>
        </span>
        <button type="button" onClick={() => setForcerLeChoix(true)} className="font-semibold text-accent">
          Changer d'espace
        </button>
      </p>
    ) : null

  return (
    <AddSheet
      spaceId={espace}
      depart={depart}
      entete={entete}
      onClose={() => void navigate(`/espace/${espace}`, { replace: true })}
    />
  )
}

/**
 * À quel espace ce partage est-il destiné ?
 *
 * Posée seulement quand aucun espace n'est actif : autrement l'espace actif fait foi, et le
 * bandeau du formulaire permet d'en changer d'un appui. Demander à chaque partage reviendrait à
 * rendre lent ce qu'on vient de rendre rapide.
 */
function ChoixDEspace({
  me,
  partage,
  onChoix,
}: {
  me: Me
  partage: string | null
  onChoix: (spaceId: string) => void
}) {
  return (
    <main
      className="mx-auto min-h-dvh bg-surface pb-12"
      style={{ maxWidth: layout.column, paddingInline: layout.gutter }}
    >
      <header className="flex items-center" style={{ height: layout.header }}>
        <h1 className="font-serif text-screen">Partager vers…</h1>
      </header>

      {partage ? (
        <p className="truncate text-meta text-ink-muted" style={{ maxWidth: '100%' }}>
          {partage}
        </p>
      ) : null}

      {me.spaces.length === 0 ? (
        <p className="mt-6 text-body text-ink-muted">
          Vous n'avez pas encore d'espace. Créez-en un, puis repartagez.
        </p>
      ) : (
        <ul className="mt-6 flex flex-col gap-3">
          {me.spaces.map((espace) => (
            <li key={espace.id}>
              <button
                type="button"
                onClick={() => onChoix(espace.id)}
                className="flex w-full items-center gap-3 rounded-card border border-border bg-card p-3 text-left"
                style={{ minHeight: layout.minimumTouchTarget }}
              >
                <CategoryMark initials={espace.emblemInitials} hue={espace.emblemHue} size="medium" />
                <span className="text-card-title font-semibold">{espace.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </main>
  )
}
